import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, Dropdown } from 'antd';
import { useDefaultLandingPageStore, usePromptStore } from '@globalStore/defaultLandingPageStore';
import { useDefaultLandingPageMutation } from '@hooks/useDefaultLandingPage';
import commonSelector, { isAdminRoleSelector, isStaffRoleSelector } from '@store/common/selectors';
import { setActiveDashboardId } from '@store/dashboards/ui/actions';
import getActualShareLink from '@components/common/Sharing/SharingModal/SharingContent/getActualShareLink';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import featureNames from '@utils/featureToggle/constants';
import { FormActionContext } from '../../../form/context';
import ToggleIcon from '@icons/common/toggle.svg';
import { ToggleButton } from '@src/components/common/Utils/Buttons';

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;

  button {
    margin-right: 16px;
  }
`;

const PageButtons = () => {
  const {
    actions: { onCreate },
  } = React.useContext(FormActionContext);
  const dispatch = useDispatch();
  const { features, profiles } = useSelector(commonSelector);
  const isAdmin = useSelector(isAdminRoleSelector);
  const isStaff = useSelector(isStaffRoleSelector);
  const canCreateAlert = isAdmin || isStaff;
  const cid = profiles.filter((p) => p.is_selected)[0].customer_id;

  // Get state from Zustand
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    path: state.path,
    name: state.name,
  }));
  // Use the hook to get the mutation function
  const { setDefaultLandingPage: mutateLandingPage } = useDefaultLandingPageMutation();

  // Get actions from prompt store
  const promptActions = usePromptStore((state) => ({
    setShowPrompt: state.setShowPrompt,
    setPromptState: state.setPromptState,
  }));

  const setLandingPageEnabled = features[featureNames.DEFAULT_LANDING_PAGE] || true;
  const { pathname } = window.location;
  const defaultPathname = defaultLandingPage.path || '';
  const isDefaultLandingPage = defaultPathname.endsWith(pathname);

  const onSetDefaultLandingApply = () => {
    const u = getActualShareLink(pathname, cid);
    // Use the mutation to persist to server
    mutateLandingPage({
      url: u,
      name: 'Alert',
      path: pathname,
    });
  };

  const hasDefaultLandingPage = defaultLandingPage.url.length > 0;
  const dlpContent = hasDefaultLandingPage
    ? 'Are you sure you want to change your default dashboard?'
    : 'Are you sure you want to set your default dashboard?';
  const dlpOkFn = () => onSetDefaultLandingApply();
  const onSetDefaultLanding = () => {
    dispatch(setActiveDashboardId(null));
    // Use Zustand actions
    promptActions.setPromptState({
      promptNeedInput: false,
      promptTitle: 'Make default',
      promptContent: dlpContent,
      promptOkFn: dlpOkFn,
    });
    promptActions.setShowPrompt(true);
  };

  const onApply = ({ key: kebabId }: any) => {
    switch (kebabId) {
      case 'default': {
        onSetDefaultLanding();
        break;
      }
    }
  };

  return (
    <Wrapper>
      {canCreateAlert && (
        <Button type="primary" onClick={onCreate}>
          Create Alert
        </Button>
      )}
      {setLandingPageEnabled && (
        <Dropdown
          menu={{
            items: [{ key: 'default', label: 'Make Default', disabled: isDefaultLandingPage }],
            onClick: onApply,
          }}
          trigger={['click']}
        >
          <ToggleButton icon={<ToggleIcon />} />
        </Dropdown>
      )}
      <DefaultLandingPrompt />
    </Wrapper>
  );
};

export default PageButtons;
